import { FormEvent, useState } from 'react';

import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { FormField } from '../../components/FormField';
import { Input } from '../../components/Input';
import {
  FormBlock,
  FormError,
  ForgotRow,
  Heading,
  HeadingAccent,
  HeadingBlock,
  InlineLink,
  InsetField,
  SignUpRow,
} from './SignInModal.styles';
import type { SignInFormProps, SignInModalLabels } from './SignInModal.types';

export const DEFAULT_LABELS: Required<SignInModalLabels> = {
  title: 'Sign in.',
  subtitle: 'Good things come to those who log in.',
  signUpPrompt: "Don't have an account?",
  signUpLink: 'Sign up',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  submit: 'Sign in',
  forgottenPassword: 'Forgotten password?',
  close: 'Close',

  signUpTitle: 'Sign up.',
  signUpSubtitle: 'Fast now. Faster next time.',
  signInPrompt: 'Already have an account?',
  signInLink: 'Sign in',
  isSpaMember: 'Already a spa member?',
  spaMemberNumberLabel: 'Spa Member No',
  signUpSubmit: 'Sign up',
};

export function SignInForm({
  values,
  onChange,
  onSubmit,
  isSubmitting = false,
  errors,
  signUpValues,
  onSignUpChange,
  onSignUpSubmit,
  isSigningUp = false,
  signUpErrors,
  onForgottenPasswordClick,
  labels,
  mode: controlledMode,
  onModeChange,
  insetLabels = false,
}: SignInFormProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };

  const renderField = ({
    type,
    value,
    onChange: onFieldChange,
    label,
    error,
    autoComplete,
  }: {
    type: string;
    value: string;
    onChange: (value: string) => void;
    label: string;
    error?: string;
    autoComplete?: string;
  }) => {
    const field = (
      <Input
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onFieldChange(e.target.value)}
        error={Boolean(error)}
        aria-label={label}
        placeholder={insetLabels ? label : undefined}
      />
    );

    if (insetLabels) {
      return (
        <InsetField>
          {field}
          {error && <FormError role="alert">{error}</FormError>}
        </InsetField>
      );
    }

    return (
      <FormField label={label} required error={error}>
        {field}
      </FormField>
    );
  };
  const [internalMode, setInternalMode] = useState<'signIn' | 'signUp'>('signIn');
  const mode = controlledMode ?? internalMode;

  const changeMode = (next: 'signIn' | 'signUp') => {
    onModeChange?.(next);
    if (controlledMode === undefined) setInternalMode(next);
  };

  const isSignUp = mode === 'signUp' && Boolean(signUpValues && onSignUpChange && onSignUpSubmit);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSignUp) {
      if (isSigningUp) return;
      onSignUpSubmit?.();
      return;
    }
    if (isSubmitting) return;
    onSubmit();
  };

  return (
    <>
      <HeadingBlock>
        {isSignUp ? (
          <>
            <Heading>
              {merged.signUpTitle} <HeadingAccent>{merged.signUpSubtitle}</HeadingAccent>
            </Heading>
            <SignUpRow>
              {merged.signInPrompt}{' '}
              <InlineLink type="button" onClick={() => changeMode('signIn')}>
                {merged.signInLink}
              </InlineLink>
            </SignUpRow>
          </>
        ) : (
          <>
            <Heading>
              {merged.title} <HeadingAccent>{merged.subtitle}</HeadingAccent>
            </Heading>
            <SignUpRow>
              {merged.signUpPrompt}{' '}
              <InlineLink type="button" onClick={() => changeMode('signUp')}>
                {merged.signUpLink}
              </InlineLink>
            </SignUpRow>
          </>
        )}
      </HeadingBlock>

      <FormBlock onSubmit={handleSubmit} noValidate>
        {isSignUp && signUpValues && onSignUpChange ? (
          <>
            {renderField({
              type: 'email',
              autoComplete: 'email',
              value: signUpValues.email,
              onChange: (value) => onSignUpChange('email', value),
              label: merged.emailLabel,
              error: signUpErrors?.email,
            })}

            <Checkbox
              checked={signUpValues.isSpaMember}
              onChange={(checked) => onSignUpChange('isSpaMember', checked)}
              label={merged.isSpaMember}
            />

            {signUpValues.isSpaMember &&
              renderField({
                type: 'text',
                value: signUpValues.spaMemberNumber,
                onChange: (value) => onSignUpChange('spaMemberNumber', value),
                label: merged.spaMemberNumberLabel,
                error: signUpErrors?.spaMemberNumber,
              })}

            {signUpErrors?.form && <FormError role="alert">{signUpErrors.form}</FormError>}

            <Button type="submit" variant="primary" fullWidth disabled={isSigningUp}>
              {merged.signUpSubmit}
            </Button>
          </>
        ) : (
          <>
            {renderField({
              type: 'email',
              autoComplete: 'email',
              value: values.email,
              onChange: (value) => onChange('email', value),
              label: merged.emailLabel,
              error: errors?.email,
            })}

            {renderField({
              type: 'password',
              autoComplete: 'current-password',
              value: values.password,
              onChange: (value) => onChange('password', value),
              label: merged.passwordLabel,
              error: errors?.password,
            })}

            {errors?.form && <FormError role="alert">{errors.form}</FormError>}

            <Button type="submit" variant="primary" fullWidth disabled={isSubmitting}>
              {merged.submit}
            </Button>
          </>
        )}
      </FormBlock>

      {!isSignUp && onForgottenPasswordClick && (
        <ForgotRow>
          <InlineLink type="button" onClick={onForgottenPasswordClick}>
            {merged.forgottenPassword}
          </InlineLink>
        </ForgotRow>
      )}
    </>
  );
}
